import { useState, useEffect, Fragment } from "react";
import { createPortal } from "react-dom";
import { ShoppingCart, Plus, ArrowRight, Clock, Trash2, Store, Search, Printer } from "lucide-react";
import { toast } from "sonner";
import DatePicker from "@/components/ui/DatePicker";
import SearchableSelect from "@/components/ui/SearchableSelect";
import api, { errMsg } from "../lib/api";
import { fmtDateTime12 } from "../lib/datetime";
import useAutoRefresh from "../lib/useAutoRefresh";

export default function ProcurementPage() {
  const [pos, setPos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [skus, setSkus] = useState([]);
  const [reorderAlerts, setReorderAlerts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [vendorId, setVendorId] = useState("");
  const [expectedDelivery, setExpectedDelivery] = useState("");
  const [lines, setLines] = useState([{ sku_id: "", quantity: 1, unit_cost: 0 }]);
  const [loading, setLoading] = useState(true);
  // Bulk-raise selection: checkbox per row + per-row qty override
  const [selected, setSelected] = useState(new Set());
  const [qtyOver, setQtyOver] = useState({});
  // Registry detail: which PO row is expanded (lines + history inline)
  const [expandedPo, setExpandedPo] = useState(null);
  // Vendor-wise grouping of the registry: which vendor card is open
  const [openVendorCard, setOpenVendorCard] = useState(null);
  // One vendor filter for the whole window: vendor cards + reorder products
  const [vendorFilter, setVendorFilter] = useState("");
  // Free-text product search over the Out / Low list
  const [itemSearch, setItemSearch] = useState("");
  // Inside the Create PO modal, keep the product picker to the chosen vendor
  const [filterToVendor, setFilterToVendor] = useState(true);

  useEffect(() => {
    Promise.all([
      fetchPOs(),
      fetchVendors(),
      fetchSkus(),
      fetchReorderAlerts()
    ]).finally(() => setLoading(false));
  }, []);

  const fetchPOs = async () => {
    try {
      const res = await api.get("/procurement/purchase-orders");
      setPos(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await api.get("/vendors");
      setVendors(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchSkus = async () => {
    try {
      const res = await api.get("/inventory/skus");
      setSkus(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchReorderAlerts = async () => {
    try {
      const res = await api.get("/inventory/purchase-orders");
      // Keep ALL drafts here; the Out / Low gate is applied in visibleAlerts
      // below so well-stocked SKUs the velocity formula flags never appear.
      setReorderAlerts(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  // POs are drafted and stocked from other screens — keep this list current
  // without asking anyone to press a refresh button.
  useAutoRefresh(
    () => Promise.all([fetchPOs(), fetchVendors(), fetchSkus(), fetchReorderAlerts()]),
    30000
  );

  // Vendor name (what the reorder draft carries) -> vendor id (what a PO needs).
  // Drafts now carry vendor_id straight from the SKU link; the name match is
  // only a fallback, because sheet names ("SALON BASICS", "L'OREAL INDIA
  // PRIVATE LIMITED") never equal register names ("SALON BASIC", "L'Oreal")
  // and rows silently fell out of the raise flow.
  const vendorIdFor = (alert) => {
    if (alert?.vendor_id) return alert.vendor_id;
    const key = (alert?.vendor || "").trim().toLowerCase();
    if (!key) return "";
    const hit = (vendors || []).find((v) => (v.name || "").trim().toLowerCase() === key);
    return hit ? hit.id : "";
  };
  const vendorNameFor = (vendorId) =>
    (vendors || []).find((v) => v.id === vendorId)?.name || "Vendor";

  // Matches the Vendor Matrix: only vendors supplying current inventory
  // appear in the dropdowns. Legacy register names with zero linked SKUs
  // (DE FAB, KANPEKI, KERASTASE, NAN, QOD) stay out of both lists.
  const liveVendors = (skus || []).length
    ? (vendors || []).filter((v) => (skus || []).some((s) => s.vendor_id && s.vendor_id === v.id))
    : (vendors || []);

  // Does this reorder row belong to `vid`? A row carries the vendor NAME while
  // a filter carries the vendor ID, so match either — rows whose vendor name
  // never made it into the vendor master would otherwise be unreachable.
  const matchesVendor = (alert, vid) => {
    if (!vid) return true;
    if (vendorIdFor(alert) === vid) return true;
    const name = (vendorNameFor(vid) || "").trim().toLowerCase();
    return !!name && name !== "vendor" && (alert.vendor || "").trim().toLowerCase() === name;
  };

  // PO statuses are free text on the backend, so recognise rather than compare.
  const statusTone = (s) => {
    const k = String(s || "draft").toLowerCase();
    if (k.includes("cancel")) return "bg-rose-100 text-rose-900 border-rose-300";
    if (k.includes("receiv") || k.includes("deliver") || k.includes("complete") || k.includes("close"))
      return "bg-emerald-100 text-emerald-900 border-emerald-300";
    if (k.includes("approv") || k.includes("sent") || k.includes("transit"))
      return "bg-sky-100 text-sky-900 border-sky-300";
    if (k.includes("draft") || k.includes("pend")) return "bg-slate-100 text-slate-700 border-slate-300";
    return "bg-amber-100 text-amber-900 border-amber-300";
  };

  const poLineSum = (po) => (po.lines || []).reduce((s, l) => s + ((l.unit_cost || 0) * (l.quantity || 0)), 0);
  const poTotal = (po) => (po.total ?? poLineSum(po)) || 0;

  // The registry is grouped VENDOR-WISE: one card per vendor, click to open.
  // A PO with no vendor id still gets a card, keyed by its name, so nothing
  // disappears just because the vendor master is missing a row.
  const vendorCardKey = (po) =>
    po.vendor_id || `name:${(po.vendor_name || "").trim().toLowerCase() || "unknown"}`;
  const vendorCards = (() => {
    const groups = new Map();
    (pos || []).forEach((po) => {
      const key = vendorCardKey(po);
      if (!groups.has(key)) groups.set(key, { key, name: po.vendor_name || "Vendor", pos: [] });
      groups.get(key).pos.push(po);
    });
    return [...groups.values()]
      .filter((g) => !vendorFilter || g.key === vendorFilter)
      .map((g) => {
        const totals = {};
        g.pos.forEach((p) => {
          const st = p.status || "Draft";
          totals[st] = (totals[st] || 0) + 1;
        });
        const items = new Set();
        g.pos.forEach((p) => (p.lines || []).forEach((l) => items.add(l.sku_name || l.sku_id)));
        return {
          ...g,
          total: g.pos.reduce((s, p) => s + poTotal(p), 0),
          statuses: Object.entries(totals).sort((a, b) => b[1] - a[1]),
          items: items.size,
        };
      })
      .sort((a, b) => b.pos.length - a.pos.length || a.name.localeCompare(b.name));
  })();
  const openVendor = vendorCards.find((g) => g.key === openVendorCard) || null;

  // Vendor-wise register on paper. Everything the screen shows is in this one
  // block — one section per vendor, its POs and every line under them — so the
  // printed sheet and the registry can never disagree. `window.print()` blocks
  // until the dialog closes, so the 30-second auto-refresh cannot re-render the
  // page out from under an open print job.
  const printRegister = () => {
    if (!vendorCards.length) return;
    window.print();
  };

  // Same Out / Low definition as the Inventory hub: one main stock.
  const stockStatusOf = (alert) => {
    const sku = (skus || []).find((s) => s.id === alert.sku_id);
    const onHand = sku
      ? (Number(sku.store_qty) || 0)
      : (alert.on_hand || 0);
    const min = sku ? sku.min_stock ?? 0 : 0;
    if (onHand === 0) return { label: "Out of Stock", onHand };
    if (min > 0 && onHand <= min) return { label: "Low Stock", onHand };
    return { label: "Active", onHand };
  };

  // This window lists Out-of-Stock and Low-Stock rows ONLY, then narrowed to
  // the chosen vendor and the typed item so one vendor's products — or one
  // product — can be found without scrolling 1200 rows.
  const itemQuery = itemSearch.trim().toLowerCase();
  const visibleAlerts = (reorderAlerts || [])
    .map((a) => ({ ...a, _status: stockStatusOf(a).label, _onHand: stockStatusOf(a).onHand }))
    .filter((a) => a._status !== "Active")
    .filter((a) => matchesVendor(a, vendorFilter))
    .filter(
      (a) =>
        !itemQuery ||
        (a.sku_name || "").toLowerCase().includes(itemQuery) ||
        (a.category || "").toLowerCase().includes(itemQuery)
    );
  const selectableAlerts = visibleAlerts.filter((a) => vendorIdFor(a));
  const outCount = visibleAlerts.filter((a) => a._status === "Out of Stock").length;
  const lowCount = visibleAlerts.filter((a) => a._status === "Low Stock").length;

  const qtyFor = (alert) =>
    qtyOver[alert.sku_id] !== undefined ? qtyOver[alert.sku_id] : (alert.suggested_order_qty || 0);

  const toggleOne = (skuId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(skuId)) next.delete(skuId);
      else next.add(skuId);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) =>
      prev.size === selectableAlerts.length ? new Set() : new Set(selectableAlerts.map((a) => a.sku_id))
    );
  };

  // Raise one draft PO per vendor for the selected rows.
  const handleRaisePOs = async () => {
    const rows = selectableAlerts.filter((a) => selected.has(a.sku_id));
    if (rows.length === 0) {
      toast.error("Select at least one product (rows without a vendor cannot be raised)");
      return;
    }
    const groups = {};
    rows.forEach((a) => {
      const vid = vendorIdFor(a);
      (groups[vid] = groups[vid] || []).push(a);
    });
    try {
      const created = [];
      for (const [vid, items] of Object.entries(groups)) {
        const poLines = items
          .map((a) => ({ sku_id: a.sku_id, quantity: Number(qtyFor(a)) || 0, unit_cost: a.unit_cost || 0 }))
          .filter((l) => l.quantity > 0);
        if (poLines.length === 0) continue;
        const res = await api.post("/procurement/purchase-orders", {
          vendor_id: vid,
          expected_delivery: expectedDelivery || null,
          lines: poLines,
        });
        created.push(`${res.data?.po_number || "PO"} (${vendorNameFor(vid)}, ${poLines.length} items)`);
      }
      if (created.length === 0) {
        toast.error("Nothing to raise — selected quantities are all zero");
        return;
      }
      toast.success(`Raised ${created.length} PO(s): ${created.join(" · ")}`);
      setSelected(new Set());
      setQtyOver({});
      fetchPOs();
      fetchReorderAlerts();
    } catch (e) {
      toast.error(errMsg(e));
      fetchPOs();
      fetchReorderAlerts();
    }
  };

  const handleCreatePO = async (e) => {
    e.preventDefault();
    if (!vendorId) {
      toast.error("Please select a vendor");
      return;
    }
    const validLines = lines.filter((l) => l.sku_id && l.quantity > 0);
    if (validLines.length === 0) {
      toast.error("Please select at least one valid line item with quantity");
      return;
    }

    try {
      const res = await api.post("/procurement/purchase-orders", {
        vendor_id: vendorId,
        expected_delivery: expectedDelivery || null,
        lines: validLines,
      });
      toast.success(`Purchase order ${res.data?.po_number || ""} generated successfully!`.trim());
      setShowModal(false);
      setVendorId("");
      setExpectedDelivery("");
      setLines([{ sku_id: "", quantity: 1, unit_cost: 0 }]);
      fetchPOs();
    } catch (e) {
      console.error(e);
      toast.error(errMsg(e));
    }
  };

  const addLineRow = () => {
    setLines([...lines, { sku_id: "", quantity: 1, unit_cost: 0 }]);
  };

  const generatePoForAlert = (alert) => {
    // The reorder draft carries the vendor NAME; the PO needs the vendor ID.
    setVendorId(vendorIdFor(alert));
    setLines([{ sku_id: alert.sku_id, quantity: alert.suggested_order_qty || 1, unit_cost: alert.unit_cost || 0 }]);
    setShowModal(true);
  };

  // Create PO modal: once a vendor is chosen the product picker narrows to
  // that vendor's products, so a vendor's range can be picked by filtering
  // rather than typed out of a list of hundreds. Lines already on the order
  // stay visible, otherwise picking a filter would blank out a selection.
  const modalVendorName = vendorId ? vendorNameFor(vendorId).trim().toLowerCase() : "";
  const vendorSkus = vendorId
    ? skus.filter(
        (s) =>
          s.vendor_id === vendorId ||
          (!!modalVendorName &&
            modalVendorName !== "vendor" &&
            (s.vendor_name || "").trim().toLowerCase() === modalVendorName)
      )
    : [];
  const vendorSkuIds = new Set(vendorSkus.map((s) => s.id));
  const pickedIds = new Set(lines.map((l) => l.sku_id).filter(Boolean));
  const vendorOnly = !!vendorId && filterToVendor && vendorSkus.length > 0;
  const modalSkus = vendorOnly
    ? skus.filter((s) => vendorSkuIds.has(s.id) || pickedIds.has(s.id))
    : skus;

  if (loading) return (
    <div className="p-6 sm:p-10 max-w-7xl mx-auto space-y-8 animate-pulse">
      <div className="h-10 bg-slate-200 rounded w-1/3 mb-4"></div>
      <div className="h-48 bg-slate-200 rounded-xl w-full"></div>
      <div className="h-96 bg-slate-200 rounded-xl w-full"></div>
    </div>
  );

  return (
    <div className="p-6 sm:p-10 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-300">
      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-200 pb-6">
        <div>
          <div className="lss-overline">Supply Chain Operations</div>
          <h1 className="text-3xl font-extrabold text-slate-950 flex items-center gap-3 mt-1 tracking-tight">
            <ShoppingCart className="w-7 h-7 text-slate-900" /> Procurement & PO Management
          </h1>
          <p className="text-slate-600 font-medium text-sm mt-1">
            Lead time reordering, MOQ compliance, and shipment delivery tracking
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={printRegister}
            disabled={!vendorCards.length}
            title={
              vendorCards.length
                ? "Print the purchase order register, grouped vendor-wise"
                : "No purchase orders to print yet"
            }
            className="lss-btn-outline px-4 py-2.5 text-xs font-extrabold uppercase tracking-wider flex items-center gap-2 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Printer className="w-4 h-4" /> Export Vendor-wise
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="lss-btn-gold px-4 py-2.5 text-xs font-extrabold uppercase tracking-wider flex items-center gap-2 shadow-sm shrink-0"
          >
            <Plus className="w-4 h-4" /> Create Purchase Order
          </button>
        </div>
      </div>

      {/* Raised POs Register — one card per vendor, click a card to open it */}
      <div className="lss-card p-6 space-y-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <h2 className="text-lg font-extrabold text-slate-950">Purchase Order Registry</h2>
            <p className="text-xs font-semibold text-slate-500 mt-0.5">
              Ordered vendor-wise — click a vendor card to open its purchase orders
            </p>
          </div>
          <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200 tabular">
            {(pos || []).length} orders · {vendorCards.length} vendors
          </span>
        </div>

        {vendorCards.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-sm font-medium border border-dashed border-slate-300 rounded-xl bg-white/60">
            {pos?.length ? `No purchase orders for ${vendorNameFor(vendorFilter)}.` : "No purchase orders found."}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {vendorCards.map((g) => {
              const isOpen = g.key === openVendorCard;
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => setOpenVendorCard(isOpen ? null : g.key)}
                  title={isOpen ? `Close ${g.name}` : `Open ${g.name}'s purchase orders`}
                  className={`text-left rounded-xl border p-4 transition-all cursor-pointer ${
                    isOpen
                      ? "border-amber-400 bg-amber-50 ring-2 ring-amber-400/30 shadow-md"
                      : "border-slate-200 bg-white hover:border-amber-300 hover:bg-amber-50/50 hover:shadow-sm"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-extrabold text-slate-950 flex items-center gap-1.5">
                        <Store className="w-4 h-4 text-amber-600 shrink-0" />
                        <span className="truncate">{g.name}</span>
                      </div>
                      <div className="text-[11px] font-semibold text-slate-500 mt-0.5 tabular">
                        {g.pos.length} order{g.pos.length === 1 ? "" : "s"} · {g.items} product
                        {g.items === 1 ? "" : "s"}
                      </div>
                    </div>
                    <span
                      className={`text-[10px] font-extrabold uppercase tracking-wide shrink-0 ${
                        isOpen ? "text-amber-800" : "text-slate-400"
                      }`}
                    >
                      {isOpen ? "Close" : "Open"}
                    </span>
                  </div>
                  <div className="mt-3 flex items-end justify-between gap-2">
                    <span className="text-base font-extrabold text-emerald-800 tabular">
                      ₹{g.total.toLocaleString("en-IN")}
                    </span>
                    <span className="flex flex-wrap gap-1 justify-end">
                      {g.statuses.slice(0, 3).map(([st, n]) => (
                        <span
                          key={st}
                          className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold border ${statusTone(st)}`}
                        >
                          {n} {st}
                        </span>
                      ))}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {openVendor && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-xl bg-slate-950 text-amber-300">
            <div className="font-extrabold text-sm flex items-center gap-2 min-w-0">
              <Store className="w-4 h-4 text-amber-400 shrink-0" />
              <span className="truncate">{openVendor.name}</span>
              <span className="font-semibold text-amber-200/80 tabular">
                — {openVendor.pos.length} order{openVendor.pos.length === 1 ? "" : "s"} · ₹
                {openVendor.total.toLocaleString("en-IN")}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setOpenVendorCard(null)}
              className="text-[11px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-lg bg-amber-400/15 hover:bg-amber-400/30 text-amber-200 transition shrink-0"
            >
              Close
            </button>
          </div>
        )}

        {/* The table body stays at its original depth on purpose: the wrapper is
            always in the DOM and only styled out of sight while no card is
            open, so the per-PO rows below keep their own indentation. */}
        <div
          className={`overflow-x-auto rounded-xl ${
            openVendor ? "border border-amber-300/70" : "border border-slate-200 hidden"
          }`}
        >
          <table className="lss-table text-xs">
            <thead>
              <tr className="bg-slate-50 text-slate-700">
                <th className="py-3 px-4 text-left">PO Number</th>
                <th className="py-3 px-4 text-left">Vendor</th>
                <th className="py-3 px-4 text-center">Items</th>
                <th className="py-3 px-4 text-right">Total Cost</th>
                <th className="py-3 px-4 text-center">Status</th>
                <th className="py-3 px-4 text-center">Delivery Date</th>
                <th className="py-3 px-4 text-center">Created At</th>
              </tr>
            </thead>
            <tbody>
              {openVendor ? openVendor.pos.map((po) => {
                const lineSum = (po.lines || []).reduce((sum, l) => sum + ((l.unit_cost || 0) * (l.quantity || 0)), 0);
                const totalCost = (po.total ?? lineSum) || 0;
                const isOpen = expandedPo === po.id;
                const hist = po.status_history || [];
                return (
                  <Fragment key={po.id}>
                    <tr key={po.id} className="hover:bg-slate-50 transition">
                      <td className="py-3 px-4">
                        <button
                          onClick={() => setExpandedPo(isOpen ? null : po.id)}
                          className="font-bold text-sky-800 hover:text-sky-950 hover:underline tabular flex items-center gap-1"
                          title={isOpen ? "Hide details" : "Show PO details"}
                        >
                          <span className="text-[10px] text-slate-400">{isOpen ? "▼" : "▶"}</span>
                          {po.po_number}
                        </button>
                      </td>
                      <td className="py-3 px-4 font-semibold text-slate-800">{po.vendor_name || "Vendor"}</td>
                      <td className="py-3 px-4 text-center font-bold text-slate-700">{po.lines?.length || 0} SKUs</td>
                      <td className="py-3 px-4 text-right font-extrabold text-emerald-800 tabular">
                        ₹{totalCost.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span
                          className={`px-2.5 py-1 rounded-full text-[11px] font-extrabold border ${statusTone(po.status)}`}
                        >
                          {po.status || "Draft"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center text-slate-600 font-medium">{po.expected_delivery || "N/A"}</td>
                      <td className="py-3 px-4 text-center text-slate-500 font-medium">
                        {po.created_at ? fmtDateTime12(po.created_at) : "—"}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr key={`${po.id}-detail`} className="bg-slate-50/70">
                        <td colSpan={7} className="px-6 py-4">
                          <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11px] font-semibold text-slate-600 mb-3">
                            <span>Vendor: <strong className="text-slate-900">{po.vendor_name || "—"}</strong></span>
                            <span>Status: <strong className="text-slate-900">{po.status || "draft"}</strong></span>
                            <span>Raised by: <strong className="text-slate-900">{po.created_by || "—"}</strong></span>
                            {po.expected_delivery && <span>Expected: <strong className="text-slate-900">{po.expected_delivery}</strong></span>}
                            {po.actual_delivery && <span>Delivered: <strong className="text-slate-900">{po.actual_delivery}</strong></span>}
                            {po.notes && <span className="w-full text-slate-500">Note: {po.notes}</span>}
                          </div>
                          <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white max-h-72 overflow-y-auto">
                            <table className="lss-table text-xs min-w-[640px]">
                              <thead>
                                <tr className="bg-slate-950 text-amber-300">
                                  <th className="py-2 px-3 text-center w-12">#</th>
                                  <th className="py-2 px-3 text-left">Product</th>
                                  <th className="py-2 px-3 text-right">Qty</th>
                                  <th className="py-2 px-3 text-right">Unit Cost</th>
                                  <th className="py-2 px-3 text-right">Line Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(po.lines || []).map((l, i) => (
                                  <tr key={l.id || i} className="border-t border-slate-100">
                                    <td className="py-1.5 px-3 text-center text-slate-400 tabular">{i + 1}</td>
                                    <td className="py-1.5 px-3 font-semibold text-slate-800">{l.sku_name || l.sku_id}</td>
                                    <td className="py-1.5 px-3 text-right tabular">{l.quantity}</td>
                                    <td className="py-1.5 px-3 text-right tabular">₹{(l.unit_cost || 0).toLocaleString()}</td>
                                    <td className="py-1.5 px-3 text-right tabular font-bold">
                                      ₹{((l.quantity || 0) * (l.unit_cost || 0)).toLocaleString()}
                                    </td>
                                  </tr>
                                ))}
                                {(po.lines || []).length === 0 && (
                                  <tr><td colSpan={5} className="text-center py-4 text-slate-400">No lines on this PO.</td></tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                          {hist.length > 0 && (
                            <div className="mt-2 text-[11px] text-slate-500 font-medium">
                              History: {hist.map((h) => `${h.status}${h.changed_by ? ` by ${h.changed_by}` : ""}${h.timestamp ? ` (${fmtDateTime12(h.timestamp)})` : ""}`).join("  →  ")}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              }) : null}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lead Time & Reorder — row-wise, Out / Low stock only, tick to raise */}
      <div className="lss-card p-6 border-l-4 border-amber-500 bg-amber-50/30 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-extrabold text-slate-950 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-700" /> Raise Purchase Order — Out of Stock & Low Stock
          </h2>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-200 text-amber-900 tabular">
              {outCount} Out · {lowCount} Low
            </span>
            <button
              onClick={handleRaisePOs}
              disabled={selected.size === 0}
              className="lss-btn-gold px-4 py-1.5 text-xs font-extrabold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Raise PO ({selected.size})
            </button>
          </div>
        </div>

        {/* Vendor + item filters sit directly above the list they narrow.
            The vendor one is shared: it also groups the registry cards above. */}
        <div className="flex flex-wrap items-end gap-3 bg-white border border-amber-200/80 rounded-xl p-3">
          <div className="w-56">
            <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1">
              Vendor
            </div>
            <SearchableSelect
              options={[{ id: "", name: "All vendors" }, ...liveVendors]}
              value={vendorFilter}
              onChange={(val) => {
                setVendorFilter(val);
                setOpenVendorCard(null);
                setSelected(new Set());
              }}
              placeholder="All vendors"
              getOptionLabel={(v) => v.name}
              getOptionValue={(v) => v.id}
            />
          </div>

          <div className="flex-1 min-w-[220px]">
            <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1">
              Search items
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                type="search"
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                placeholder="Type a product name or category to filter..."
                className="lss-input lss-search w-full text-xs font-semibold"
              />
            </div>
          </div>

          {(vendorFilter || itemSearch) && (
            <button
              type="button"
              onClick={() => {
                setVendorFilter("");
                setItemSearch("");
                setOpenVendorCard(null);
                setSelected(new Set());
              }}
              className="lss-btn-outline px-3 py-2 text-xs font-extrabold uppercase tracking-wider shrink-0"
            >
              Clear
            </button>
          )}

          <span className="ml-auto text-xs font-extrabold text-slate-700 tabular">
            {visibleAlerts.length} items
          </span>
        </div>

        {visibleAlerts.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-sm font-medium border border-dashed border-slate-300 rounded-xl bg-white/60">
            {itemQuery
              ? `No items match "${itemSearch.trim()}".`
              : vendorFilter
                ? `No out-of-stock or low-stock items for ${vendorNameFor(vendorFilter)}.`
                : "No out-of-stock or low-stock items. Everything is covered."}
          </div>
        ) : (
          <div className="overflow-x-auto border border-amber-200/80 rounded-xl bg-white">
            <table className="lss-table min-w-[900px] text-xs">
              <thead>
                <tr className="bg-slate-950 text-amber-300">
                  <th className="py-2.5 px-3 text-center w-10">
                    <input
                      type="checkbox"
                      checked={selectableAlerts.length > 0 && selected.size === selectableAlerts.length}
                      onChange={toggleAll}
                      title="Select all"
                    />
                  </th>
                  <th className="py-2.5 px-3 text-left">Product</th>
                  <th className="py-2.5 px-3 text-left">Vendor</th>
                  <th className="py-2.5 px-3 text-right">On Hand</th>
                  <th className="py-2.5 px-3 text-center">Status</th>
                  <th className="py-2.5 px-3 text-right">Reorder Pt</th>
                  <th className="py-2.5 px-3 text-right">Order Qty</th>
                  <th className="py-2.5 px-3 text-right">Est. Cost</th>
                  <th className="py-2.5 px-3 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {visibleAlerts.map((alert) => {
                  const vid = vendorIdFor(alert);
                  const qty = qtyFor(alert);
                  const est = (Number(qty) || 0) * (alert.unit_cost || 0);
                  const isOut = alert._status === "Out of Stock";
                  return (
                    <tr key={alert.sku_id} className="hover:bg-slate-50/80 border-t border-slate-100">
                      <td className="py-2.5 px-3 text-center">
                        <input
                          type="checkbox"
                          checked={selected.has(alert.sku_id)}
                          disabled={!vid}
                          title={vid ? "Select for bulk PO" : "No vendor linked — assign one in Inventory first"}
                          onChange={() => toggleOne(alert.sku_id)}
                        />
                      </td>
                      <td className="py-2.5 px-3 font-extrabold text-slate-950">{alert.sku_name}</td>
                      <td className="py-2.5 px-3 font-semibold text-slate-700">{alert.vendor || "—"}</td>
                      <td className="py-2.5 px-3 text-right tabular font-bold text-slate-800">{alert._onHand}</td>
                      <td className="py-2.5 px-3 text-center">
                        <span className={`lss-badge text-[10px] uppercase ${isOut ? "bg-rose-100 text-rose-900 border-rose-300" : "bg-amber-100 text-amber-950 border-amber-300"}`}>
                          {isOut ? "Out of Stock" : "Low Stock"}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-right tabular text-slate-600">{alert.reorder_point}</td>
                      <td className="py-2.5 px-3 text-right">
                        <input
                          type="number"
                          min="0"
                          value={qty}
                          onChange={(e) => setQtyOver((prev) => ({ ...prev, [alert.sku_id]: parseFloat(e.target.value) || 0 }))}
                          className="lss-input w-20 text-xs font-bold text-right tabular px-2 py-1"
                        />
                      </td>
                      <td className="py-2.5 px-3 text-right tabular font-bold text-emerald-900">₹{est.toLocaleString("en-IN")}</td>
                      <td className="py-2.5 px-3 text-center">
                        <button
                          onClick={() => generatePoForAlert(alert)}
                          className="py-1 px-3 bg-amber-100 hover:bg-amber-200 text-amber-950 font-extrabold text-[11px] rounded-lg transition inline-flex items-center gap-1"
                        >
                          Draft PO <ArrowRight className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {selected.size > 0 && (
          <div className="text-xs font-bold text-slate-600">
            {selected.size} selected — one draft PO is raised per vendor on Raise.
          </div>
        )}
      </div>


      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 overflow-y-auto" onClick={() => setShowModal(false)}>
          <div className="bg-white border border-slate-200 shadow-2xl rounded-2xl p-6 sm:p-8 max-w-4xl min-h-[500px] w-full space-y-5 text-slate-900 my-auto overflow-visible" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <h3 className="text-xl font-extrabold text-slate-950">Generate Purchase Order</h3>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-slate-400 hover:text-slate-700 text-lg font-bold p-1 rounded-lg hover:bg-slate-100"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreatePO} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Vendor</label>
                <SearchableSelect
                  options={liveVendors}
                  value={vendorId}
                  onChange={(val) => {
                    setVendorId(val);
                    // A new vendor means a new range: start filtered again.
                    setFilterToVendor(true);
                  }}
                  placeholder="Select Vendor..."
                  getOptionLabel={(v) => `${v.name} (Lead Time: ${v.lead_time_days} days)`}
                  getOptionValue={(v) => v.id}
                />
                {vendorId && (
                  <div className="mt-1.5 flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <span className="text-[11px] font-semibold text-slate-600">
                      {vendorOnly
                        ? `Showing ${vendorSkus.length} of ${skus.length} products from ${vendorNameFor(vendorId)}`
                        : vendorSkus.length === 0
                          ? `No products linked to ${vendorNameFor(vendorId)} yet — showing all ${skus.length}`
                          : `Showing all ${skus.length} products`}
                    </span>
                    <button
                      type="button"
                      onClick={() => setFilterToVendor((v) => !v)}
                      className="text-[11px] font-extrabold text-amber-800 hover:text-amber-950 hover:underline shrink-0"
                    >
                      {vendorOnly
                        ? "Show all products"
                        : vendorSkus.length > 0
                          ? `Only ${vendorNameFor(vendorId)}'s`
                          : "Show all"}
                    </button>
                  </div>
                )}
              </div>

              <DatePicker
                label="Expected Delivery Date"
                value={expectedDelivery}
                onChange={(e) => setExpectedDelivery(e.target.value)}
                className="w-full mt-1"
              />

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wide">Line Items</label>
                  <span className="text-[11px] font-semibold text-slate-500">
                    {lines.length} items · {modalSkus.length} products available
                  </span>
                </div>

                {/* Column labels sit above the list rather than inside each row:
                    the row boxes are narrow, and a label per row would triple the
                    height of the order. Widths mirror the rows exactly — including
                    the remove-button slot — so the three columns line up whether or
                    not a row is removable, and they stay put while the list scrolls. */}
                <div className="flex items-center gap-2 px-2 pb-1.5 mb-1 border-b border-slate-200/80">
                  <div className="flex-1 min-w-0">
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                      Product / Item
                    </span>
                  </div>
                  <div className="w-20 shrink-0 text-center">
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                      Qty
                    </span>
                  </div>
                  <div className="w-28 shrink-0 text-right">
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                      Unit Cost ₹
                    </span>
                  </div>
                  <div className="w-7 shrink-0" aria-hidden="true" />
                </div>

                <div className="space-y-2.5 max-h-72 overflow-y-auto pr-1">
                  {lines.map((l, idx) => (
                    <div key={idx} className="flex items-center gap-2 w-full min-w-0 bg-slate-50/70 p-2 rounded-xl border border-slate-200/80">
                      <div className="flex-1 min-w-0">
                        <SearchableSelect
                          options={modalSkus}
                          value={l.sku_id}
                          onChange={(val, selectedSku) => {
                            const copy = [...lines];
                            copy[idx].sku_id = val;
                            if (selectedSku) copy[idx].unit_cost = selectedSku.unit_cost || 0;
                            setLines(copy);
                          }}
                          placeholder="Type product name to search..."
                          getOptionLabel={(s) => s.name}
                          getOptionValue={(s) => s.id}
                        />
                      </div>

                      <div className="w-20 shrink-0">
                        <input
                          type="number"
                          placeholder="Qty"
                          aria-label={`Line ${idx + 1} quantity`}
                          value={l.quantity}
                          onChange={(e) => {
                            const copy = [...lines];
                            copy[idx].quantity = parseFloat(e.target.value) || 0;
                            setLines(copy);
                          }}
                          className="lss-input w-full text-xs font-semibold bg-white text-center"
                        />
                      </div>

                      <div className="w-28 shrink-0">
                        <input
                          type="number"
                          placeholder="Unit Cost ₹"
                          aria-label={`Line ${idx + 1} unit cost`}
                          value={l.unit_cost}
                          onChange={(e) => {
                            const copy = [...lines];
                            copy[idx].unit_cost = parseFloat(e.target.value) || 0;
                            setLines(copy);
                          }}
                          className="lss-input w-full text-xs font-semibold bg-white text-right"
                        />
                      </div>

                      {/* Fixed-width slot so the Qty / Unit Cost columns stay lined
                          up with the labels above whether or not this row can be
                          removed — the button alone would shift them by 28px. */}
                      <div className="w-7 shrink-0 flex items-center justify-center">
                        {lines.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const copy = lines.filter((_, i) => i !== idx);
                              setLines(copy);
                            }}
                            className="p-1.5 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-rose-50 transition shrink-0"
                            title="Remove Line Item"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={addLineRow}
                  className="text-xs font-bold text-amber-800 hover:text-amber-950 mt-3 flex items-center gap-1 hover:underline cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" /> Add Line Item
                </button>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="lss-btn-outline px-5 py-2.5 text-xs font-extrabold uppercase tracking-wider"
                >
                  Cancel
                </button>
                <button type="submit" className="lss-btn-gold px-5 py-2.5 text-xs font-extrabold uppercase tracking-wider">
                  Create PO
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Vendor-wise register. Never visible on screen — the print stylesheet is
          the only thing that reveals it — and portalled to <body> so it is a
          direct child, which is exactly what `body > *:not(#po-print-report)`
          needs in order to hide the app, the sidebar and the toasts on paper. */}
      {createPortal(
        <div id="po-print-report">
          <h1>Purchase Order Register — Vendor-wise</h1>
          <p className="po-print-sub">
            Geetanjali · {vendorCards.length} vendor{vendorCards.length === 1 ? "" : "s"} ·{" "}
            {vendorCards.reduce((s, g) => s + g.pos.length, 0)} purchase order
            {vendorCards.reduce((s, g) => s + g.pos.length, 0) === 1 ? "" : "s"}
            {vendorFilter && vendorCards.length ? ` · filtered to ${vendorCards[0].name}` : ""}
          </p>
          <p className="po-print-sub">Generated {fmtDateTime12(new Date())} IST</p>
          <hr className="po-print-rule" />

          {vendorCards.map((g) => (
            <section key={g.key} className="po-print-vendor">
              <h2>
                <span>{g.name}</span>
                <span>
                  {g.pos.length} order{g.pos.length === 1 ? "" : "s"} · {g.items} product
                  {g.items === 1 ? "" : "s"} · ₹{g.total.toLocaleString("en-IN")}
                </span>
              </h2>

              <table>
                <thead>
                  <tr>
                    <th>PO Number</th>
                    <th className="ctr">Status</th>
                    <th className="ctr">Items</th>
                    <th className="ctr">Delivery</th>
                    <th className="ctr">Created</th>
                    <th className="num">Total Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {g.pos.map((po) => {
                    const lineSum = (po.lines || []).reduce(
                      (sum, l) => sum + (l.unit_cost || 0) * (l.quantity || 0),
                      0
                    );
                    const totalCost = (po.total ?? lineSum) || 0;
                    return (
                      <Fragment key={po.id}>
                        <tr>
                          <td>
                            <strong>{po.po_number}</strong>
                            {po.created_by ? <div className="po-print-note">by {po.created_by}</div> : null}
                          </td>
                          <td className="ctr">{po.status || "Draft"}</td>
                          <td className="ctr">{(po.lines || []).length}</td>
                          <td className="ctr">{po.expected_delivery || "—"}</td>
                          <td className="ctr">{po.created_at ? fmtDateTime12(po.created_at) : "—"}</td>
                          <td className="num">
                            <strong>₹{totalCost.toLocaleString("en-IN")}</strong>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={6} className="po-print-lines-cell">
                            <table className="po-print-lines">
                              <thead>
                                <tr>
                                  <th className="ctr">#</th>
                                  <th>Product</th>
                                  <th className="num">Qty</th>
                                  <th className="num">Unit Cost</th>
                                  <th className="num">Line Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(po.lines || []).map((l, i) => (
                                  <tr key={l.id || i}>
                                    <td className="ctr">{i + 1}</td>
                                    <td>{l.sku_name || l.sku_id}</td>
                                    <td className="num">{l.quantity}</td>
                                    <td className="num">₹{(l.unit_cost || 0).toLocaleString("en-IN")}</td>
                                    <td className="num">
                                      ₹{((l.quantity || 0) * (l.unit_cost || 0)).toLocaleString("en-IN")}
                                    </td>
                                  </tr>
                                ))}
                                {(po.lines || []).length === 0 && (
                                  <tr>
                                    <td colSpan={5} className="ctr">
                                      No lines on this order.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                            {po.notes && <p className="po-print-note">Note: {po.notes}</p>}
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>

              <div className="po-print-subtotal">
                Vendor subtotal: ₹{g.total.toLocaleString("en-IN")}
              </div>
            </section>
          ))}

          <div className="po-print-total">
            <span>Grand total — all vendors</span>
            <span>₹{vendorCards.reduce((s, g) => s + g.total, 0).toLocaleString("en-IN")}</span>
          </div>

          <p className="po-print-foot">
            Status history and the full order timeline stay on screen in the Procurement window.
            All amounts in Indian Rupees (₹).
          </p>
        </div>,
        document.body
      )}
    </div>
  );
}
